import { ListPlus, X } from 'lucide-react';
import { useId, useState, type ReactElement } from 'react';

import { cn } from '@/components/ui/cn';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useToast } from '@/components/ui/toast';
import type { DossierNorm } from '@/features/document-dossier/types';
import { usePermission } from '@/hooks/usePermission';
import type { DocumentStatus } from '@/lib/document-status';
import { normBadgeClass } from '@/lib/norm-badge';
import { plural } from '@/lib/plural';

import { classificationError, clauseLabel } from '../presentation';
import { useUnclassify } from '../use-classification';
import { ClassifyModal } from './ClassifyModal';

// Cláusulas de norma de la ficha (multinorma, QA F1 #13): las que cumple el documento,
// para encontrarlo por requisito en una auditoría. Calidad agrega y quita cláusulas;
// quitar pide confirmación. Mismo patrón que Documentos relacionados: un documento
// anulado no admite cláusulas nuevas, pero sí quitar las que tenía.

export function ClassificationCard({
  entryId,
  code,
  status,
  norms,
}: {
  entryId: string;
  code: string;
  status: DocumentStatus;
  norms: DossierNorm[];
}): ReactElement {
  const titleId = useId();
  const canClassify = usePermission('quality:document:classify');
  const unclassify = useUnclassify(entryId);
  const { showToast } = useToast();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<DossierNorm | null>(null);
  const canAdd = canClassify && status !== 'anulado';

  async function confirmRemove(): Promise<void> {
    if (removing === null) {
      return;
    }
    try {
      await unclassify.mutateAsync(removing.id);
      showToast(`Cláusula ${clauseLabel(removing)} quitada de ${code}`);
      setRemoving(null);
    } catch (err) {
      showToast(classificationError(err), 'error');
    }
  }

  return (
    <section className="card rise d3" aria-labelledby={titleId}>
      <div className="ch">
        <h3 id={titleId}>Cláusulas de norma vinculadas</h3>
        {norms.length > 0 && (
          <span className="count">{plural(norms.length, 'cláusula', 'cláusulas')}</span>
        )}
        {canAdd && (
          <button type="button" className="btn mut sm" onClick={() => setAdding(true)}>
            <ListPlus size={14} aria-hidden="true" />
            Agregar cláusula
          </button>
        )}
      </div>
      {norms.length === 0 ? (
        <div className="cb">
          <p className="soft" style={{ fontSize: 13, margin: 0 }}>
            Sin cláusulas de norma vinculadas todavía.
          </p>
        </div>
      ) : (
        <div className="t-scroll">
          <table className="t">
            <thead>
              <tr>
                <th scope="col">Norma</th>
                <th scope="col">Cláusula</th>
                <th scope="col">Requisito</th>
                {canClassify && (
                  <th scope="col">
                    <span className="sr-only">Acciones</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {norms.map((n) => (
                <tr key={n.id} aria-label={clauseLabel(n)}>
                  <td>
                    <span className={cn('badge', normBadgeClass(n.normCode))}>{n.normCode}</span>
                  </td>
                  <td className="code">{n.clause}</td>
                  <td>{n.requirement}</td>
                  {canClassify && (
                    <td className="t-act">
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label={`Quitar ${clauseLabel(n)}`}
                        onClick={() => setRemoving(n)}
                      >
                        <X size={14} aria-hidden="true" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ClassifyModal
        open={adding}
        entryId={entryId}
        code={code}
        linkedNormIds={norms.map((n) => n.id)}
        onClose={() => setAdding(false)}
      />
      <ConfirmModal
        open={removing !== null}
        title="Quitar cláusula"
        message={
          removing === null
            ? ''
            : `${code} dejará de estar clasificado en ${clauseLabel(removing)}. Ya no aparecerá al filtrar por esa cláusula en la Lista Maestra. Puedes volver a agregarla.`
        }
        confirmLabel="Quitar"
        destructive
        busy={unclassify.isPending}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setRemoving(null)}
      />
    </section>
  );
}
